<template>
  <div>
    <b-modal id="modal-drawing-accident" ref="canvasModal" @shown="onModalShown" centered hide-footer no-close-on-backdrop
      no-close-on-esc size="lg" :body-class="'px-3 pt-2'" :footer-class="'d-block border-0 pt-0 pb-3 m-0'"
      :header-class="'align-items-center'">
      <template #modal-header="{ close }">
        <h5 class="modal-title">
          {{ $t('drawingAccident.title') }}
        </h5>
        <b-button size="sm" class="d-none" variant="outline-danger" @click="close()">
          Close Modal
        </b-button>

      </template>
      <div ref="container" class="w-100 h-100" style="min-height: 660px;">
        <v-stage ref="stage" :config="stageConfig" @mousedown="handleStageMouseDown" @mousemove="handleStageMouseMove"
          @mouseup="handleStageMouseUp">
          <!-- Main layer for shapes -->
          <v-layer ref="shapesLayer">
            <!-- All shapes -->
            <v-rect v-for="(shape, index) in shapes.filter(s => s.type === 'rect')" :key="'rect-' + index"
              :config="shape.config" />
            <v-line v-for="(shape, index) in shapes.filter(s => s.type === 'line')" :key="'line-' + index"
              :config="shape.config" />
            <v-line v-for="(shape, index) in shapes.filter(s => s.type === 'free')" :key="'free-' + index"
              :config="shape.config" />
          </v-layer>

          <!-- Temporary layer for eraser preview -->
          <v-layer ref="tempLayer">
            <v-line v-if="eraserPath" :config="eraserPath" />
          </v-layer>
        </v-stage>
      </div>
      <div class="toolbar">
        <div class="d-flex justify-content-center flex-wrap">
          <button @click="setTool('free')">{{ $t('drawingAccident.freeDraw') }} | </button>
          <button class="ml-2" @click="setTool('rect')">{{ $t('drawingAccident.rectangle') }} | </button>
          <button class="ml-2" @click="setTool('line')">{{ $t('drawingAccident.line') }} | </button>
          <button class="ml-2" @click="setTool('eraser')">{{ $t('drawingAccident.eraser') }} | </button>
          <button class="ml-2" @click="clearCanvas">{{ $t('drawingAccident.clear') }} | </button>
          <div class="d-flex align-items-center ml-2">
            <label class="mb-0 mr-2">{{ $t('drawingAccident.color') }} </label>
            <input type="color" class="mr-1" v-model="currentColor"> |
          </div>
          <div class="d-flex align-items-center ml-2">
            <label class="mb-0 mr-2">{{ $t('drawingAccident.size') }} </label>
            <input type="range" class="mr-1" v-model="currentSize" min="1" max="50">
          </div>
        </div>
        <div class="d-flex justify-content-end align-items-center mt-2">
          <b-button class="close-btn mr-3" variant="secondary" @click="clearCanvas();$bvModal.hide('modal-drawing-accident')"> {{ $t('drawingAccident.cancel') }}
          </b-button>
          <b-button class="save-btn" variant="primary" @click="getStageJson"> {{ $t('drawingAccident.save') }} </b-button>
        </div>
      </div>
    </b-modal>
  </div>
</template>
<script>
import Konva from 'konva';
export default {
  name: "DrawingAccident",
  props: ['drawingData'],
  data() {
    return {
      stageConfig: {},
      currentTool: 'free',
      isDrawing: false,
      shapes: [],
      eraserPath: null,
      isErasing: false,
      currentColor: '#df4b26',
      currentSize: 5,
      eraserSize: 20
    }
  },
  methods:{
    getStageJson() {
      const stage = this.$refs.stage.getStage();

      const background = new Konva.Rect({
        x: 0,
        y: 0,
        width: stage.width(),
        height: stage.height(),
        fill: 'white',
        listening: false
      });

      const firstLayer = stage.getLayers()[0];
      firstLayer.add(background);
      background.moveToBottom();

      // Redraw and export
      stage.draw();
      const dataURL = stage.toDataURL();
      this.$emit("getAccidentDrawing", {
        image:dataURL,
        jsonData: JSON.stringify(this.shapes),
      })
      this.clearCanvas()
      this.$bvModal.hide('modal-drawing-accident')
    },

    setTool(tool) {
      this.currentTool = tool;
      this.isDrawing = false;
      this.isErasing = false;

      const container = this.$refs.stage.getNode().container();
      if (tool === 'eraser') {
        container.style.cursor = `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24'><path fill='%23000000' d='M15.14,3C14.63,3 14.12,3.2 13.73,3.59L2.59,14.73C1.81,15.5 1.81,16.77 2.59,17.56L5.03,20H12.69L20.41,12.27C21.2,11.5 21.2,10.23 20.41,9.44L16.56,5.59C16.17,5.2 15.65,3 15.14,3M17,18L15,20H22V18'/></svg>") 0 24, auto`;
      } else {
        container.style.cursor = 'crosshair';
      }
    },

    handleStageMouseDown(e) {
      if (!this.currentTool) return;

      const pos = e.target.getStage().getPointerPosition();

      if (this.currentTool === 'eraser') {
        this.isErasing = true;
        this.eraserPath = {
          points: [pos.x, pos.y],
          //   stroke: 'rgba(0,0,0,0.5)',
          strokeWidth: this.eraserSize,
          lineCap: 'round',
          lineJoin: 'round',
          listening: false
        };
        this.detectIntersections(pos);
        return;
      }

      this.isDrawing = true;

      const newShape = {
        type: this.currentTool,
        config: {
          id: Date.now().toString(),
          draggable: true
        }
      };

      switch (this.currentTool) {
        case 'free':
          newShape.config.points = [pos.x, pos.y];
          newShape.config.stroke = this.currentColor;
          newShape.config.strokeWidth = this.currentSize;
          newShape.config.lineCap = 'round';
          newShape.config.lineJoin = 'round';
          break;

        case 'rect':
          newShape.config.x = pos.x;
          newShape.config.y = pos.y;
          newShape.config.width = 0;
          newShape.config.height = 0;
          newShape.config.fill = this.currentColor;
          newShape.config.stroke = this.currentColor;
          newShape.config.strokeWidth = 1;
          break;

        case 'line':
          newShape.config.points = [pos.x, pos.y, pos.x, pos.y];
          newShape.config.stroke = this.currentColor;
          newShape.config.strokeWidth = this.currentSize;
          newShape.config.lineCap = 'round';
          newShape.config.lineJoin = 'round';
          break;
      }

      this.shapes.push(newShape);
    },

    handleStageMouseMove(e) {
      if (!this.isDrawing && !this.isErasing) return;

      const pos = e.target.getStage().getPointerPosition();

      if (this.isErasing) {
        this.eraserPath.points = this.eraserPath.points.concat([pos.x, pos.y]);
        this.detectIntersections(pos);
        this.$refs.tempLayer.getNode().batchDraw();
        return;
      }

      const lastShape = this.shapes[this.shapes.length - 1];

      switch (lastShape.type) {
        case 'free':
          lastShape.config.points = lastShape.config.points.concat([pos.x, pos.y]);
          break;

        case 'rect':
          lastShape.config.width = pos.x - lastShape.config.x;
          lastShape.config.height = pos.y - lastShape.config.y;
          break;

        case 'line':
          lastShape.config.points = [
            lastShape.config.points[0],
            lastShape.config.points[1],
            pos.x,
            pos.y
          ];
          break;
      }

      this.$refs.shapesLayer.getNode().batchDraw();
    },

    handleStageMouseUp() {
      if (this.isErasing) {
        this.isErasing = false;
        this.eraserPath = null;
        this.$refs.tempLayer.getNode().batchDraw();
        return;
      }

      this.isDrawing = false;
    },

    clearCanvas() {
      this.shapes = [];
      this.$refs.shapesLayer.getNode().batchDraw();
    },

    detectIntersections(pos) {
      const stage = this.$refs.stage.getNode();
      const eraserArea = {
        x: pos.x - this.eraserSize / 2,
        y: pos.y - this.eraserSize / 2,
        width: this.eraserSize,
        height: this.eraserSize
      };

      // Check all shapes for intersection with eraser
      this.shapes = this.shapes.filter(shape => {
        const shapeNode = stage.findOne('#' + shape.config.id);
        if (!shapeNode) return true;

        if (shape.type === 'free') {
          // For free drawing, check if any point is within eraser area
          for (let i = 0; i < shape.config.points.length; i += 2) {
            const x = shape.config.points[i];
            const y = shape.config.points[i + 1];
            if (x > eraserArea.x && x < eraserArea.x + eraserArea.width &&
              y > eraserArea.y && y < eraserArea.y + eraserArea.height) {
              return false; // Remove this shape
            }
          }
          return true;
        } else {
          // For rectangles and lines, use Konva's intersection check
          return !shapeNode.intersects(eraserArea);
        }
      });

      this.$refs.shapesLayer.getNode().batchDraw();
    },
    onModalShown() {
      this.$nextTick(() => {
        const container = this.$refs.container;
        const rect = container.getBoundingClientRect();
        this.stageConfig = {
          width: rect.width,
          height: rect.height
        };
      });
    }
  },
  watch:{
    drawingData(newVal) {
      if (newVal) {
        this.shapes = JSON.parse(newVal.jsonData);
        this.$refs.shapesLayer.getNode().batchDraw();
      }
    }
  }
}
</script>
<style scoped>
.toolbar {
  padding: 10px;
  background: #f0f0f0;
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  gap: 10px;
}
</style>